import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";

interface IndexDatum {
  val: number;
  pct: number;
}
interface Indices {
  nifty: IndexDatum;
  sensex: IndexDatum;
  bnf: IndexDatum;
}

const formatNumber = (v: number) =>
  v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const itemsFrom = (indices: Indices) => [
  { label: "NIFTY 50", d: indices.nifty },
  { label: "SENSEX", d: indices.sensex },
  { label: "BANK NIFTY", d: indices.bnf },
];

const CarouselIndices: React.FC<{ indices: Indices; autoplayMs?: number }> = ({
  indices,
  autoplayMs = 4000,
}) => {
  const items = itemsFrom(indices);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const activeRef = useRef(active);
  activeRef.current = active;

  const isHoveringRef = useRef(false);
  const autoplayRef = useRef<number | null>(null);

  // Robust scrollIntoView centering
  const scrollToIndex = (index: number) => {
    const container = containerRef.current;
    if (!container) return;
    const cards = Array.from(
      container.querySelectorAll<HTMLElement>("[data-carousel-card]"),
    );
    if (!cards.length) return;
    const target = cards[index];
    if (!target) return;
    
    // Use scrollIntoView for smooth centering
    target.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
    setActive(index);
  };

  const next = () => scrollToIndex((activeRef.current + 1) % items.length);
  const prev = () =>
    scrollToIndex((activeRef.current - 1 + items.length) % items.length);

  // Autoplay
  useEffect(() => {
    if (!autoplayMs || autoplayMs <= 0) return;
    if (autoplayRef.current) window.clearInterval(autoplayRef.current);
    autoplayRef.current = window.setInterval(() => {
      if (!isHoveringRef.current) next();
    }, autoplayMs);
    return () => {
      if (autoplayRef.current) window.clearInterval(autoplayRef.current);
    };
  }, [autoplayMs, items.length]);

  // Update active on manual scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const cards = Array.from(
          container.querySelectorAll<HTMLElement>("[data-carousel-card]"),
        );
        if (!cards.length) return;
        const containerRect = container.getBoundingClientRect();
        const centerX = containerRect.left + containerRect.width / 2;
        let closestIndex = 0;
        let closestDist = Infinity;
        cards.forEach((c, i) => {
          const r = c.getBoundingClientRect();
          const cCenter = r.left + r.width / 2;
          const dist = Math.abs(centerX - cCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closestIndex = i;
          }
        });
        if (closestIndex !== activeRef.current) setActive(closestIndex);
      });
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Center first card on mount
  useEffect(() => {
    const t = setTimeout(() => scrollToIndex(0), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="w-full border-b border-white/5 bg-black/20 py-2 relative">
      {/* Left Arrow */}
      <div className="absolute left-2 top-1/2 -translate-y-1/2 z-20">
        <button
          aria-label="Previous"
          onClick={prev}
          className="p-2 rounded-full bg-black/60 hover:bg-black/80 text-white/90 shadow-lg backdrop-blur-sm transition-all focus:outline-none focus:ring-2 focus:ring-accent-primary"
        >
          <ArrowLeft size={18} />
        </button>
      </div>

      {/* Right Arrow */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 z-20">
        <button
          aria-label="Next"
          onClick={next}
          className="p-2 rounded-full bg-black/60 hover:bg-black/80 text-white/90 shadow-lg backdrop-blur-sm transition-all focus:outline-none focus:ring-2 focus:ring-accent-primary"
        >
          <ArrowRight size={18} />
        </button>
      </div>

      {/* Scroll container */}
      <div
        ref={containerRef}
        onMouseEnter={() => (isHoveringRef.current = true)}
        onMouseLeave={() => (isHoveringRef.current = false)}
        className="flex gap-2 px-8 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth"
        style={{ 
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {items.map((item, i) => {
          const positive = item.d.pct >= 0;
          return (
            <div
              key={i}
              data-carousel-card
              role="group"
              aria-roledescription="slide"
              aria-label={`${item.label} ${i + 1} of ${items.length}`}
              className="snap-center bg-white/[0.03] border border-white/5 rounded-2xl px-4 py-1 min-w-[200px] max-w-[200px] backdrop-blur-md transition-all hover:scale-105 hover:bg-white/[0.06] hover:border-white/10 flex-shrink-0"
            >
              <p className="text-[10px] font-bold text-gray-400 tracking-widest mb-2 uppercase">
                {item.label}
              </p>

              <div className="flex items-end gap-2 mb-3">
                <span className="text-base font-bold font-mono tabular-nums text-white">
                  {formatNumber(item.d.val)}
                </span>

                <span
                  className={`text-xs flex items-center font-black ${positive ? "text-emerald-400" : "text-rose-400"}`}
                >
                  {positive ? (
                    <ArrowUpRight size={14} />
                  ) : (
                    <ArrowDownRight size={14} />
                  )}
                  <span className="ml-0.5 tabular-nums">
                    {Math.abs(Number(item.d.pct)).toFixed(2)}%
                  </span>
                </span>
              </div>

              {/* Mini chart */}
              <div className="mt-2 h-6">
                <svg viewBox="0 0 100 24" className="w-full h-full" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id={`gradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={positive ? "#10b981" : "#fb7185"}
                        stopOpacity="0.3"
                      />
                      <stop
                        offset="100%"
                        stopColor={positive ? "#10b981" : "#fb7185"}
                        stopOpacity="0.05"
                      />
                    </linearGradient>
                  </defs>
                  <path
                    d={
                      positive
                        ? "M0,18 L25,14 L50,10 L75,8 L100,10 L100,24 L0,24 Z"
                        : "M0,8 L25,10 L50,14 L75,16 L100,14 L100,24 L0,24 Z"
                    }
                    fill={`url(#gradient-${i})`}
                  />
                  <path
                    d={
                      positive
                        ? "M0,18 L25,14 L50,10 L75,8 L100,10"
                        : "M0,8 L25,10 L50,14 L75,16 L100,14"
                    }
                    fill="none"
                    stroke={positive ? "#10b981" : "#fb7185"}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      {/* Indicators */}
      <div className="mt-3 flex justify-center gap-2">
        {items.map((_, i) => (
          <button
            key={i}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => scrollToIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === active 
                ? "bg-accent-primary w-6" 
                : "bg-white/20 w-1.5 hover:bg-white/40"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default CarouselIndices;
